"use client";

import { cn } from "@saridana-core/react-ui-kit";
import { TProductRequirementItem } from "@shared/types";
import { domToReact, HTMLReactParserOptions } from "html-react-parser";
import { HtmlParser, ReplacementsMap } from "../html-parser";
import { mapAttribsToProps } from "@shared/utils";

export function ProductRequirementItems({
  items,
  indexClassName,
  textClassName,
  contentVariant = "default",
}: {
  items: TProductRequirementItem[];
  indexClassName?: string;
  textClassName?: string;
  contentVariant?: "default" | "primary";
}) {
  const contentVariantMap = {
    default: {
      background: "bg-white",
      border: "border-gray-200",
    },
    primary: {
      background: "bg-white",
      border: "border-primary-200",
    },
  };

  const replacementsMap: ReplacementsMap = {
    figure: (domNode: any, options: HTMLReactParserOptions) => {
      const attribs = domNode.attribs || {};
      const props = mapAttribsToProps(attribs);

      const mergedClass = cn("relative w-full min-w-full", contentVariantMap[contentVariant].border, props.className);
      delete props.className;

      return (
        <figure {...props} className={mergedClass}>
          {domToReact(domNode.children, options)}
        </figure>
      );
    },
    table: (domNode: any, options: HTMLReactParserOptions) => {
      const attribs = domNode.attribs || {};
      const props = mapAttribsToProps(attribs);

      const mergedClass = cn(
        "relative w-[calc(100%+2px)] min-w-full -ml-[1px] -my-[1px]",
        contentVariantMap[contentVariant].border,
        props.className,
      );
      delete props.className;

      return (
        <div
          className={cn(
            "rounded-lg overflow-hidden border",
            contentVariantMap[contentVariant].background,
            contentVariantMap[contentVariant].border,
          )}
        >
          <table {...props} className={mergedClass}>
            {domToReact(domNode.children, options)}
          </table>
        </div>
      );
    },
    tr: (domNode: any, options: HTMLReactParserOptions) => {
      const attribs = domNode.attribs || {};
      const props = mapAttribsToProps(attribs);

      return <tr {...props}>{domToReact(domNode.children, options)}</tr>;
    },
    td: (domNode: any, options: HTMLReactParserOptions) => {
      const attribs = domNode.attribs || {};
      const props = mapAttribsToProps(attribs);

      const mergedClass = cn("text-gray-900 p-4 border", contentVariantMap[contentVariant].border, props.className);
      delete props.className;

      return (
        <td {...props} className={mergedClass}>
          {domToReact(domNode.children, options)}
        </td>
      );
    },
    img: (domNode: any, options: HTMLReactParserOptions) => {
      const attribs = domNode.attribs || {};
      const props = mapAttribsToProps(attribs);

      const mergedClass = cn("inline-block", props.className, props.class);
      delete props.className;

      return <img {...props} className={mergedClass} />;
    },
  };

  return (
    <div className="grid grid-cols-1 gap-10">
      {items?.map((item: TProductRequirementItem, index: number) => (
        <div key={`requirement-item-${item.id}`}>
          <div className="flex items-center gap-5">
            <div className={cn("flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 size-12 min-w-12", indexClassName)}>
              <span className="font-semibold text-xl">{index + 1}</span>
            </div>
            <h3 className={cn("font-medium text-xl leading-[140%]", textClassName)}>{item.title}</h3>
          </div>

          {item.content && (
            <div className="mt-6">
              <HtmlParser data={item.content} redefine={replacementsMap} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
